import { createStore } from 'vuex';
import { 
  getUserProfile, 
  getGlobalSettings,
  updateUserProfile,
  getUserConnections,
  getUserClasses,
  getCurriculaData
} from '@/composables/useFirestore';
import { auth } from '@/firebase/init';
import { onAuthStateChanged } from 'firebase/auth';

export default createStore({
  state: {
    user: null,
    profile: null,
    globalSettings: null,
    loading: false,
    alert: null,
    alertType: null,
    connections: [], // Connections the current user has
    pendingConnections: [], // Pending connection requests sent by the current user
    connectionRequests: [], // Connection requests received by the current user
    userClasses: [], // User's manually entered classes
    selectedConversation: null,
    curricula: null, // Add curricula to store state
    curriculaLoading: false, // Track curricula loading state
    posts: [], // Store posts data
    postsLoading: false,
    socialUsers: [], // Add this to cache discovered users
    socialUsersLoading: false, // Track loading state for social users
    socialUsersLastFetched: null, // Track when users were last fetched
    socialUsersCursor: null,
    socialUsersHasMore: true,
    socialUsersError: null,
    socialUsersQueryKey: '',
    socialUserDetailsById: {},
    semesterTests: [], // Add this to cache semester test data
    semesterTestsLoading: false, // Track loading state for semester tests
    semesterTestsLastFetched: null, // Track when semester tests were last fetched
    adminUsers: [], // Paginated list of all registered users, for the admin panel
    adminUsersLoading: false,
    adminUsersLastFetched: null,
    adminUsersCursor: null,
    adminUsersHasMore: true,
    adminUsersError: null,
    adminUsersQueryKey: '',
    adminStats: null, // { totalUsers, totalAdmins, newUsersThisWeek, facultyBreakdown, studyYearBreakdown, campusBreakdown }
    adminStatsLoading: false,
    adminStatsLastFetched: null,
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setProfile(state, profile) {
      state.profile = profile;
    },
    setGlobalSettings(state, settings) {
      state.globalSettings = settings;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setAlert(state, { message, type }) {
      state.alert = message;
      state.alertType = type;
    },
    clearAlert(state) {
      state.alert = null;
      state.alertType = null;
    },
    setConnections(state, connections) {
      state.connections = connections;
    },
    setPendingConnections(state, pendingConnections) {
      state.pendingConnections = pendingConnections;
    },
    setConnectionRequests(state, connectionRequests) {
      state.connectionRequests = connectionRequests;
    },
    addConnection(state, userId) {
      if (!state.connections.includes(userId)) {
        state.connections = [...state.connections, userId];
      }
    },
    removeConnection(state, userId) {
      state.connections = state.connections.filter(id => id !== userId);
    },
    addPendingConnection(state, userId) {
      if (!state.pendingConnections.includes(userId)) {
        state.pendingConnections = [...state.pendingConnections, userId];
      }
    },
    removePendingConnection(state, userId) {
      state.pendingConnections = state.pendingConnections.filter(id => id !== userId);
    },
    addConnectionRequest(state, userId) {
      if (!state.connectionRequests.includes(userId)) {
        state.connectionRequests = [...state.connectionRequests, userId];
      }
    },
    removeConnectionRequest(state, userId) {
      state.connectionRequests = state.connectionRequests.filter(id => id !== userId);
    },
    setUserClasses(state, classes) {
      state.userClasses = classes;
    },
    addClass(state, newClass) {
      state.userClasses.push(newClass);
    },
    updateClass(state, updatedClass) {
      const index = state.userClasses.findIndex(c => c.id === updatedClass.id);
      if (index !== -1) {
        state.userClasses.splice(index, 1, updatedClass);
      }
    },
    removeClass(state, classId) {
      state.userClasses = state.userClasses.filter(c => c.id !== classId);
    },
    setSelectedConversation(state, conversation) {
      state.selectedConversation = conversation;
    },
    clearSelectedConversation(state) {
      state.selectedConversation = null;
    },
    setCurricula(state, data) {
      state.curricula = data;
    },
    setCurriculaLoading(state, loading) {
      state.curriculaLoading = loading;
    },
    setPosts(state, posts) {
      state.posts = posts;
    },
    setPostsLoading(state, loading) {
      state.postsLoading = loading;
    },
    addPost(state, post) {
      state.posts.unshift(post); // Add new post to the beginning
    },
    updatePost(state, updatedPost) {
      const index = state.posts.findIndex(p => p.id === updatedPost.id);
      if (index !== -1) {
        state.posts.splice(index, 1, updatedPost);
      }
    },
    deletePost(state, postId) {
      state.posts = state.posts.filter(p => p.id !== postId);
    },
    // Add a like to a post
    addLikeToPost(state, { postId, userId }) {
      const post = state.posts.find(p => p.id === postId);
      if (post && !post.likes.includes(userId)) {
        post.likes.push(userId);
      }
    },
    // Remove a like from a post
    removeLikeFromPost(state, { postId, userId }) {
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        post.likes = post.likes.filter(id => id !== userId);
      }
    },
    // Add a comment to a post
    addCommentToPost(state, { postId, comment }) {
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        if (!post.comments) post.comments = [];
        post.comments.push(comment);
      }
    },
    
    // Add mutations for social users
    setSocialUsers(state, users) {
      state.socialUsers = users;
      state.socialUsersLastFetched = Date.now();
    },
    appendSocialUsers(state, users) {
      const existingIds = new Set(state.socialUsers.map(user => user.id));
      const nextCache = { ...state.socialUserDetailsById };
      users.forEach((user) => {
        if (!existingIds.has(user.id)) {
          state.socialUsers.push(user);
          existingIds.add(user.id);
        }
        if (user?.id) nextCache[user.id] = user;
      });
      state.socialUserDetailsById = nextCache;
      state.socialUsersLastFetched = Date.now();
    },
    resetSocialUsers(state) {
      state.socialUsers = [];
      state.socialUsersCursor = null;
      state.socialUsersHasMore = true;
      state.socialUsersError = null;
      state.socialUsersLastFetched = null;
    },
    setSocialUsersQueryKey(state, queryKey) {
      state.socialUsersQueryKey = queryKey;
    },
    setSocialUsersLoading(state, loading) {
      state.socialUsersLoading = loading;
    },
    setSocialUsersPagination(state, { cursor = null, hasMore = true } = {}) {
      state.socialUsersCursor = cursor;
      state.socialUsersHasMore = hasMore;
    },
    setSocialUsersError(state, error) {
      state.socialUsersError = error;
    },
    
    // Update a specific user in the cache (e.g., after connection status change)
    updateSocialUser(state, updatedUser) {
      const index = state.socialUsers.findIndex(u => u.id === updatedUser.id);
      if (index !== -1) {
        state.socialUsers.splice(index, 1, updatedUser);
      }
      state.socialUserDetailsById = {
        ...state.socialUserDetailsById,
        [updatedUser.id]: updatedUser
      };
    },
    
    // Add a new user to the cache if not already present
    addSocialUser(state, user) {
      if (!state.socialUsers.some(u => u.id === user.id)) {
        state.socialUsers.push(user);
      }
      state.socialUserDetailsById = {
        ...state.socialUserDetailsById,
        [user.id]: user
      };
    },
    cacheSocialUserDetails(state, users) {
      const nextCache = { ...state.socialUserDetailsById };
      users.forEach((user) => {
        if (user?.id) {
          nextCache[user.id] = user;
        }
      });
      state.socialUserDetailsById = nextCache;
    },
    
    // Add mutations for semester tests
    setSemesterTests(state, tests) {
      state.semesterTests = tests;
      state.semesterTestsLastFetched = Date.now();
    },
    setSemesterTestsLoading(state, loading) {
      state.semesterTestsLoading = loading;
    },

    // Add mutations for admin users page (admin panel)
    setAdminUsers(state, users) {
      state.adminUsers = users;
      state.adminUsersLastFetched = Date.now();
    },
    appendAdminUsers(state, users) {
      const existingIds = new Set(state.adminUsers.map(user => user.id));
      users.forEach((user) => {
        if (!existingIds.has(user.id)) {
          state.adminUsers.push(user);
          existingIds.add(user.id);
        }
      });
      state.adminUsersLastFetched = Date.now();
    },
    resetAdminUsers(state) {
      state.adminUsers = [];
      state.adminUsersCursor = null;
      state.adminUsersHasMore = true;
      state.adminUsersError = null;
      state.adminUsersLastFetched = null;
    },
    setAdminUsersLoading(state, loading) {
      state.adminUsersLoading = loading;
    },
    setAdminUsersPagination(state, { cursor = null, hasMore = true } = {}) {
      state.adminUsersCursor = cursor;
      state.adminUsersHasMore = hasMore;
    },
    setAdminUsersError(state, error) {
      state.adminUsersError = error;
    },
    setAdminUsersQueryKey(state, queryKey) {
      state.adminUsersQueryKey = queryKey;
    },
    setAdminUserRole(state, { uid, role }) {
      const user = state.adminUsers.find(u => u.id === uid);
      const wasAdmin = user?.role === 'admin';
      if (user) {
        user.role = role;
      }
      if (state.adminStats && wasAdmin !== (role === 'admin')) {
        state.adminStats.totalAdmins += role === 'admin' ? 1 : -1;
      }
    },
    setAdminStats(state, stats) {
      state.adminStats = stats;
      state.adminStatsLastFetched = Date.now();
    },
    setAdminStatsLoading(state, loading) {
      state.adminStatsLoading = loading;
    },
  },
  actions: {
    async fetchUser({ commit, dispatch }) {
      commit('setLoading', true);
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            console.log(`[Store] User authenticated, fetching user data for: ${user.uid}`);
            commit('setUser', { uid: user.uid, email: user.email });
            try {
              const profile = await getUserProfile(user.uid);
              console.log('[Store] User profile fetched and stored in state');
              commit('setProfile', profile);
              
              // Fetch user connections after profile is loaded
              await dispatch('fetchUserConnections');
              
              resolve(profile);
            } catch (error) {
              console.error("Error fetching user profile:", error);
              reject(error);
            }
          } else {
            console.log('[Store] No authenticated user found');
            commit('setUser', null);
            commit('setProfile', null);
            resolve(null);
          }
          commit('setLoading', false);
        });
      });
    },

    async fetchGlobalSettings({ commit }) {
      commit('setLoading', true);
      try {
        console.log('[Store] Fetching global settings');
        const settings = await getGlobalSettings();
        if (settings) {
          console.log('[Store] Global settings fetched and stored in state');
          commit('setGlobalSettings', settings);
        }
      } catch (error) {
        console.error("Error fetching global settings:", error);
      } finally {
        commit('setLoading', false);
      }
    },

    // Add a new action to fetch curricula data
    async fetchCurriculaData({ commit, state }) {
      if (state.curricula) return state.curricula; // Return existing data if available
      
      commit('setCurriculaLoading', true);
      try {
        console.log('[Store] Fetching curricula data');
        const curriculaData = await getCurriculaData();
        if (curriculaData) {
          console.log('[Store] Curricula data fetched and stored in state');
          commit('setCurricula', curriculaData);
          return curriculaData;
        }
        return null;
      } catch (error) {
        console.error("Error fetching curricula data:", error);
        // Don't show error to user - fail gracefully
        commit('setCurricula', {}); // Set empty object instead of null
        return {};
      } finally {
        commit('setCurriculaLoading', false);
      }
    },

    showAlert({ commit }, { message, type = 'info' }) {
      commit('setAlert', { message, type });
      setTimeout(() => commit('clearAlert'), 3000);
    },
    
    // Reset state (e.g., after logout)
    resetState({ commit }) {
      commit('setUser', null);
      commit('setProfile', null);
      commit('setConnections', []);
      commit('setPendingConnections', []);
      commit('setConnectionRequests', []);
    },

    // Fetch user connections from Firestore
    async fetchUserConnections({ commit, state }) {
      if (!state.user) return;
      
      commit('setLoading', true);
      try {
        const result = await getUserConnections(state.user.uid);
        
        if (result.success) {
          commit('setConnections', result.connections || []);
          commit('setPendingConnections', result.pendingConnections || []);
          commit('setConnectionRequests', result.connectionRequests || []);
          console.log('[Store] User connections updated:', {
            connections: result.connections?.length || 0,
            pending: result.pendingConnections?.length || 0,
            requests: result.connectionRequests?.length || 0
          });
        } else {
          console.error("Error fetching connections:", result.message);
        }
      } catch (error) {
        console.error("Error fetching connections:", error);
      } finally {
        commit('setLoading', false);
      }
    },
    
    // Send connection request
    async sendConnectionRequest({ commit, state }, userId) {
      if (!state.user) return false;
      
      try {
        const { sendConnectionRequest } = await import('@/composables/useFirestore');
        const result = await sendConnectionRequest(state.user.uid, userId);
        
        if (result.success) {
          commit('addPendingConnection', userId);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error sending connection request:", error);
        return false;
      }
    },
    
    // Accept connection request
    async acceptConnectionRequest({ commit, state }, requesterId) {
      if (!state.user) return false;
      
      try {
        const { acceptConnectionRequest } = await import('@/composables/useFirestore');
        const result = await acceptConnectionRequest(state.user.uid, requesterId);
        
        if (result.success) {
          commit('addConnection', requesterId);
          commit('removeConnectionRequest', requesterId);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error accepting connection request:", error);
        return false;
      }
    },
    
    // Reject connection request
    async rejectConnectionRequest({ commit, state }, requesterId) {
      if (!state.user) return false;
      
      try {
        const { rejectConnectionRequest } = await import('@/composables/useFirestore');
        const result = await rejectConnectionRequest(state.user.uid, requesterId);
        
        if (result.success) {
          commit('removeConnectionRequest', requesterId);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error rejecting connection request:", error);
        return false;
      }
    },
    
    // Cancel connection request
    async cancelConnectionRequest({ commit, state }, userId) {
      if (!state.user) return false;
      
      try {
        const { cancelConnectionRequest } = await import('@/composables/useFirestore');
        const result = await cancelConnectionRequest(state.user.uid, userId);
        
        if (result.success) {
          commit('removePendingConnection', userId);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error canceling connection request:", error);
        return false;
      }
    },
    
    // Remove connection
    async removeConnection({ commit, state }, connectionId) {
      if (!state.user) return false;
      
      try {
        const { removeConnection } = await import('@/composables/useFirestore');
        const result = await removeConnection(state.user.uid, connectionId);
        
        if (result.success) {
          commit('removeConnection', connectionId);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error removing connection:", error);
        return false;
      }
    },

    // New action to join socials
    async joinSocials({ commit, state }) {
      if (!state.user) return false;
      
      try {
        // Update the user profile in Firestore
        await updateUserProfile(state.user.uid, { joinedSocials: true });
        
        // Update local state
        commit('setProfile', { ...state.profile, joinedSocials: true });
        
        return true;
      } catch (error) {
        console.error("Error joining socials:", error);
        throw error;
      }
    },
    
    // Fetch user's manually entered classes
    async fetchUserClasses({ commit, state }) {
      if (!state.user) return;
      
      try {
        const result = await getUserClasses(state.user.uid);
        
        if (result.success) {
          commit('setUserClasses', result.classes);
          console.log('[Store] User classes loaded:', result.classes.length);
          return result.classes;
        } else {
          console.error('Error fetching user classes:', result.message);
          return [];
        }
      } catch (error) {
        console.error('Error fetching user classes:', error);
        return [];
      }
    },
    setSelectedConversation({ commit }, conversation) {
      commit('setSelectedConversation', conversation);
    },
    clearSelectedConversation({ commit }) {
      commit('clearSelectedConversation');
    },
    // Fetch posts from Firestore
    async fetchPosts({ commit, state }) {
      if (!state.user) return;
      
      commit('setPostsLoading', true);
      try {
        const { getPosts } = await import('@/composables/useFirestore');
        const result = await getPosts();
        
        if (result.success) {
          commit('setPosts', result.posts || []);
          console.log('[Store] Posts fetched:', result.posts?.length || 0);
        } else {
          console.error("Error fetching posts:", result.message);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        commit('setPostsLoading', false);
      }
    },
    
    // Create a new post
    async createPost({ commit, state }, postData) {
      if (!state.user) return false;
      
      try {
        const { createPost } = await import('@/composables/useFirestore');
        const result = await createPost({
          ...postData,
          authorId: state.user.uid,
          authorName: state.profile?.name || 'User',
          authorProfilePic: state.profile?.profilePicture || null,
          createdAt: new Date(),
          likes: [],
          comments: []
        });
        
        if (result.success) {
          commit('addPost', result.post);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error creating post:", error);
        return false;
      }
    },
    
    // Delete a post
    async deletePost({ commit, state }, postId) {
      if (!state.user) return false;
      
      try {
        const { deletePost } = await import('@/composables/useFirestore');
        const result = await deletePost(postId, state.user.uid);
        
        if (result.success) {
          commit('deletePost', postId);
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error deleting post:", error);
        return false;
      }
    },
    
    // Toggle like on a post
    async toggleLikePost({ commit, state }, postId) {
      if (!state.user) return false;
      
      try {
        const { toggleLikePost } = await import('@/composables/useFirestore');
        const post = state.posts.find(p => p.id === postId);
        
        if (!post) return false;
        
        const isLiked = post.likes && post.likes.includes(state.user.uid);
        const result = await toggleLikePost(postId, state.user.uid, !isLiked);
        
        if (result.success) {
          if (isLiked) {
            commit('removeLikeFromPost', { postId, userId: state.user.uid });
          } else {
            commit('addLikeToPost', { postId, userId: state.user.uid });
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error toggling like:", error);
        return false;
      }
    },
    
    // Add a comment to a post
    async addComment({ commit, state }, { postId, text }) {
      if (!state.user) return false;
      
      try {
        const { addComment } = await import('@/composables/useFirestore');
        const comment = {
          id: Date.now().toString(),
          authorId: state.user.uid,
          authorName: state.profile?.name || 'User',
          authorProfilePic: state.profile?.profilePicture || null,
          text,
          createdAt: new Date() // Use regular Date object instead of serverTimestamp
        };
        
        const result = await addComment(postId, comment);
        
        if (result.success) {
          commit('addCommentToPost', { postId, comment });
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error adding comment:", error);
        return false;
      }
    },
    
    // Fetch social users (people who joined socials) in pages.
    async fetchSocialUsers({ dispatch }, options = {}) {
      return dispatch('fetchSocialUsersPage', { reset: true, ...options });
    },

    async fetchSocialUsersPage(
      { commit, state },
      { force = false, reset = false, pageSize = 24, searchQuery = '', filters = {} } = {}
    ) {
      const queryKey = JSON.stringify({
        searchQuery: searchQuery.trim().toLowerCase(),
        filters: filters || {}
      });
      const queryChanged = state.socialUsersQueryKey !== queryKey;
      const cacheIsValid = state.socialUsers.length > 0 &&
        state.socialUsersLastFetched &&
        (Date.now() - state.socialUsersLastFetched) < 300000 &&
        !queryChanged;

      if (!force && !reset && !queryChanged && cacheIsValid) {
        return state.socialUsers;
      }

      if (!force && reset && !queryChanged && cacheIsValid) {
        return state.socialUsers;
      }

      if (state.socialUsersLoading) {
        return state.socialUsers;
      }

      if (reset || queryChanged) {
        commit('resetSocialUsers');
        commit('setSocialUsersQueryKey', queryKey);
      } else if (!state.socialUsersHasMore) {
        return state.socialUsers;
      }

      commit('setSocialUsersLoading', true);
      commit('setSocialUsersError', null);
      try {
        const { getSocialUsersPage } = await import('@/composables/useFirestore');
        const result = await getSocialUsersPage({
          limitCount: pageSize,
          cursorId: (reset || queryChanged) ? null : state.socialUsersCursor,
          searchQuery,
          filters
        });

        if (!result.success) {
          commit('setSocialUsersError', result.message || 'Could not load peers.');
          return state.socialUsers;
        }

        const filteredUsers = result.users.filter(user => user.id !== state.user?.uid);
        if (reset || queryChanged) {
          commit('setSocialUsers', filteredUsers);
        } else {
          commit('appendSocialUsers', filteredUsers);
        }
        commit('cacheSocialUserDetails', filteredUsers);
        commit('setSocialUsersPagination', {
          cursor: result.cursor,
          hasMore: result.hasMore
        });
        return state.socialUsers;
      } catch (error) {
        console.error("Error fetching social users:", error);
        commit('setSocialUsersError', error.message || 'Could not load peers.');
        return state.socialUsers;
      } finally {
        commit('setSocialUsersLoading', false);
      }
    },

    async fetchSocialUserDetails({ commit, state }, userIds = []) {
      const missingIds = [...new Set(userIds.filter(Boolean))]
        .filter(userId => !state.socialUserDetailsById[userId]);

      if (!missingIds.length) {
        return userIds.map(userId => state.socialUserDetailsById[userId]).filter(Boolean);
      }

      try {
        const { getUserDataBatch } = await import('@/composables/useFirestore');
        const result = await getUserDataBatch(missingIds);
        if (result.success) {
          commit('cacheSocialUserDetails', result.users);
        }
      } catch (error) {
        console.error("Error fetching social profile details:", error);
      }

      return userIds.map(userId => state.socialUserDetailsById[userId]).filter(Boolean);
    },
    
    // Update connection status in the UI without refetching all users
    updateConnectionInUI({ commit, state }, { userId, newStatus }) {
      const user = state.socialUsers.find(u => u.id === userId);
      if (user) {
        // Create a copy of the user with the updated connection status
        // This is just for UI purposes, the actual status is managed through connections arrays
        const updatedUser = { ...user, connectionStatus: newStatus };
        commit('updateSocialUser', updatedUser);
      }
    },
    
    // Add action to fetch and cache semester tests
    async fetchSemesterTests({ commit, state }, { force = false, moduleCodes = [] } = {}) {
      // Check if we already have data and it's not forced refresh
      // Use a 30-minute cache validity (1800000 ms)
      const isCacheValid = state.semesterTests.length > 0 && 
                           state.semesterTestsLastFetched && 
                           (Date.now() - state.semesterTestsLastFetched) < 1800000;
                          
      if (!force && isCacheValid && moduleCodes.length === 0) {
        console.log('[Store] Using cached semester tests');
        return state.semesterTests;
      }
      
      commit('setSemesterTestsLoading', true);
      try {
        const { fetchSemesterTestsByModules, fetchAllSemesterTests } = await import('@/composables/useTimetables');
        
        let result;
        if (moduleCodes && moduleCodes.length > 0) {
          console.log(`[Store] Fetching semester tests for ${moduleCodes.length} modules`);
          result = await fetchSemesterTestsByModules(moduleCodes);
        } else {
          console.log('[Store] Fetching all semester tests');
          result = await fetchAllSemesterTests();
        }
        
        if (result.success) {
          commit('setSemesterTests', result.data);
          console.log(`[Store] Cached ${result.data.length} semester tests`);
          return result.data;
        } else {
          console.error('[Store] Error fetching semester tests:', result.message);
          return [];
        }
      } catch (error) {
        console.error('[Store] Error fetching semester tests:', error);
        return [];
      } finally {
        commit('setSemesterTestsLoading', false);
      }
    },
    
    async updateUserPrivacySettings({ commit, state }, privacySettings) {
      if (!state.user?.uid) {
        throw new Error('User not authenticated');
      }

      try {
        const { updateUserProfile } = await import('@/composables/useFirestore');
        await updateUserProfile(state.user.uid, { privacySettings });
        commit('setProfile', { ...state.profile, privacySettings });
        return true;
      } catch (error) {
        console.error('Error updating privacy settings:', error);
        throw error;
      }
    },

    // Fetch one page of registered users for the admin panel (newest first,
    // unless a search or filter is active)
    async fetchAdminUsersPage({ commit, state }, { force = false, reset = false, pageSize = 20, searchQuery = '', filters = {} } = {}) {
      const queryKey = JSON.stringify({ searchQuery: searchQuery.trim().toLowerCase(), filters: filters || {} });
      const queryChanged = state.adminUsersQueryKey !== queryKey;

      if (state.adminUsersLoading) {
        return state.adminUsers;
      }

      if (!force && !reset && !queryChanged && !state.adminUsersHasMore && state.adminUsers.length > 0) {
        return state.adminUsers;
      }

      if (reset || queryChanged) {
        commit('resetAdminUsers');
        commit('setAdminUsersQueryKey', queryKey);
      }

      commit('setAdminUsersLoading', true);
      commit('setAdminUsersError', null);
      try {
        const { getAdminUsersPage } = await import('@/composables/useFirestore');
        const result = await getAdminUsersPage({
          limitCount: pageSize,
          cursor: (reset || queryChanged) ? null : state.adminUsersCursor,
          searchQuery,
          filters
        });

        if (!result.success) {
          commit('setAdminUsersError', result.message || 'Could not load users.');
          return state.adminUsers;
        }

        if (reset || queryChanged) {
          commit('setAdminUsers', result.users);
        } else {
          commit('appendAdminUsers', result.users);
        }
        commit('setAdminUsersPagination', { cursor: result.cursor, hasMore: result.hasMore });
        return state.adminUsers;
      } catch (error) {
        console.error('Error fetching admin users:', error);
        commit('setAdminUsersError', error.message || 'Could not load users.');
        return state.adminUsers;
      } finally {
        commit('setAdminUsersLoading', false);
      }
    },

    // Fetch quick platform stats for the admin panel (5-minute cache)
    async fetchAdminStats({ commit, state }, { force = false } = {}) {
      const cacheIsValid = state.adminStats &&
        state.adminStatsLastFetched &&
        (Date.now() - state.adminStatsLastFetched) < 300000;

      if (!force && cacheIsValid) {
        return state.adminStats;
      }

      if (state.adminStatsLoading) {
        return state.adminStats;
      }

      commit('setAdminStatsLoading', true);
      try {
        const { getAdminStats } = await import('@/composables/useFirestore');
        const result = await getAdminStats();

        if (result.success) {
          commit('setAdminStats', {
            totalUsers: result.totalUsers,
            totalAdmins: result.totalAdmins,
            newUsersThisWeek: result.newUsersThisWeek,
            facultyBreakdown: result.facultyBreakdown || [],
            studyYearBreakdown: result.studyYearBreakdown || [],
            campusBreakdown: result.campusBreakdown || []
          });
        }
        return state.adminStats;
      } catch (error) {
        console.error('Error fetching admin stats:', error);
        return state.adminStats;
      } finally {
        commit('setAdminStatsLoading', false);
      }
    },

    // Promote/demote a user's role from the admin panel
    async updateAdminUserRole({ commit, state, dispatch }, { uid, newRole }) {
      if (uid === state.user?.uid) {
        dispatch('showAlert', { message: "You can't change your own role", type: "error" });
        return false;
      }

      try {
        await updateUserProfile(uid, { role: newRole });
        commit('setAdminUserRole', { uid, role: newRole });
        dispatch('showAlert', {
          message: newRole === 'admin' ? 'User promoted to admin' : 'Admin access removed',
          type: "success"
        });
        return true;
      } catch (error) {
        console.error('Error updating user role:', error);
        dispatch('showAlert', { message: `Failed to update role: ${error.message}`, type: "error" });
        return false;
      }
    },
  },
  getters: {
    userData: (state) => state.user,
    isConnectedWith: (state) => (userId) => {
      return state.connections.includes(userId);
    },
    hasPendingRequestTo: (state) => (userId) => {
      return state.pendingConnections.includes(userId);
    },
    hasConnectionRequestFrom: (state) => (userId) => {
      return state.connectionRequests.includes(userId);
    },
    // Get connection status with another user
    connectionStatusWith: (state) => (userId) => {
      if (state.connections.includes(userId)) {
        return 'connected';
      } else if (state.pendingConnections.includes(userId)) {
        return 'pending';
      } else if (state.connectionRequests.includes(userId)) {
        return 'request';
      }
      return null;
    },
    userClassesByDay: (state) => {
      const classesByDay = {
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: []
      };
      
      state.userClasses.forEach(classItem => {
        if (classesByDay[classItem.day]) {
          classesByDay[classItem.day].push(classItem);
        }
      });
      
      // Sort classes by start time for each day
      Object.keys(classesByDay).forEach(day => {
        classesByDay[day].sort((a, b) => {
          return a.startTime.localeCompare(b.startTime);
        });
      });
      
      return classesByDay;
    },
    selectedConversation: (state) => state.selectedConversation,
    hasCurriculaData: (state) => !!state.curricula,
    
    // Get faculties from curricula
    availableFaculties: (state) => {
      if (!state.curricula) return [];
      return Object.keys(state.curricula);
    },
    // Get posts sorted by date
    sortedPosts: (state) => {
      return [...state.posts].sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
    },
    
    // Check if the current user has liked a post
    hasLikedPost: (state) => (postId) => {
      if (!state.user) return false;
      const post = state.posts.find(p => p.id === postId);
      return post && post.likes && post.likes.includes(state.user.uid);
    },
    
    // Filter social users
    filteredSocialUsers: (state) => (searchQuery = '') => {
      if (!searchQuery) return state.socialUsers;
      
      const query = searchQuery.toLowerCase();
      return state.socialUsers.filter(user => 
        (user.name && user.name.toLowerCase().includes(query)) ||
        (user.university?.faculty && user.university.faculty.toLowerCase().includes(query)) ||
        (user.university?.courseName && user.university.courseName.toLowerCase().includes(query))
      );
    },
    
    // Add getters for semester tests
    semesterTests: (state) => state.semesterTests,
    
    // Get tests for specific modules
    semesterTestsForModules: (state) => (moduleCodes) => {
      if (!Array.isArray(moduleCodes) || moduleCodes.length === 0) return [];
      
      return state.semesterTests.filter(test => {
        return moduleCodes.some(code => test.moduleCode.startsWith(code));
      });
    },
    
    // Get upcoming tests (tests that haven't happened yet)
    upcomingSemesterTests: (state) => {
      const now = new Date();
      
      // Helper function to parse test date
      const parseTestDate = (dateStr) => {
        // Parse dates in format "28 Mar 2025"
        const [day, month, year] = dateStr.split(' ');
        
        const months = {
          'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
          'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };
        
        return new Date(parseInt(year), months[month], parseInt(day));
      };
      
      return state.semesterTests
        .filter(test => {
          const testDate = parseTestDate(test.date);
          return testDate >= now;
        })
        .sort((a, b) => {
          const dateA = parseTestDate(a.date);
          const dateB = parseTestDate(b.date);
          return dateA - dateB;
        });
    },
    
    // Get the next upcoming test
    nextSemesterTest: (state, getters) => {
      const upcomingTests = getters.upcomingSemesterTests;
      return upcomingTests.length > 0 ? upcomingTests[0] : null;
    },
    
    isSemesterTestsLoading: (state) => state.semesterTestsLoading
  }
});
